import { useEffect, useRef, useState } from 'react';
import type { Media } from '@seencast/shared';
import { mediaFileUrl } from '../api/media.ts';
import { isHtmlKind } from '../api/templates.ts';
import { ScaledFrame } from './ScaledFrame.tsx';

const HTML_TILE_LABEL: Record<string, string> = {
  html_template: 'Template',
  html_snippet: 'Snippet',
};

// Inline SVGs (design rule: no emoji / no icon fonts) in the onboarding icon language
// — currentColor, loose round strokes. A slide layout for templates, code marks for
// snippets. Small (30px) so they read as a tile glyph, not empty-state illustration.
const svgBase = {
  width: 30,
  height: 30,
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.8,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
  'aria-hidden': true,
};

function HtmlKindGlyph({ kind }: { kind: string }) {
  if (kind === 'html_snippet') {
    return (
      <svg {...svgBase}>
        <path d="M8.5 8.5 4.5 12l4 3.5" />
        <path d="M15.5 8.5 19.5 12l-4 3.5" />
        <path d="M13.5 6.5l-3 11" opacity="0.55" />
      </svg>
    );
  }
  // html_template — a slide with a header bar + text lines.
  return (
    <svg {...svgBase}>
      <rect x="3" y="4.5" width="18" height="15" rx="2" />
      <path d="M3 9h18" />
      <path d="M6.5 12.5h7M6.5 15.5h10" opacity="0.55" />
    </svg>
  );
}

// Kind glyph for the non-HTML media kinds (replaces the old 🖼/🎬/🌐/📄 emoji —
// design rule: no emoji / no icon fonts). Same loose-stroke language as the
// onboarding icons: a framed photo, a play card, a globe, a document.
function KindGlyph({ kind }: { kind: string }) {
  if (kind === 'video') {
    return (
      <svg {...svgBase}>
        <rect x="3" y="5" width="18" height="14" rx="2" />
        <path d="M10.5 9.5l4 2.5-4 2.5z" />
      </svg>
    );
  }
  if (kind === 'url') {
    return (
      <svg {...svgBase}>
        <circle cx="12" cy="12" r="8.5" />
        <path d="M3.5 12h17" opacity="0.55" />
        <path d="M12 3.5c2.6 2.4 2.6 14.6 0 17c-2.6-2.4-2.6-14.6 0-17z" opacity="0.55" />
      </svg>
    );
  }
  if (kind === 'image') {
    return (
      <svg {...svgBase}>
        <rect x="3" y="4.5" width="18" height="15" rx="2" />
        <circle cx="8.5" cy="9.5" r="1.6" />
        <path d="M4 16.5l4.5-4 3.5 3 3-2.5 4 3.5" opacity="0.7" />
      </svg>
    );
  }
  // Unknown kind — a generic document.
  return (
    <svg {...svgBase}>
      <path d="M6.5 3.5h7l4.5 4.5V20.5h-11.5z" />
      <path d="M13 3.5V8.5h4.5" opacity="0.55" />
      <path d="M9 13h6M9 16h5" opacity="0.55" />
    </svg>
  );
}

/**
 * The real instantiated slide, rendered lazily once the tile scrolls into view —
 * same fetch-then-scale technique as the template gallery's <MiniPreview> and
 * <SandboxedHtmlFrame> (docs/09 §B), so a "Weekend promo" card looks the same in
 * the grid as it does everywhere else instead of a generic "TEMPLATE" tile (UX
 * audit P2-2). Falls back to the kind-tile glyph until the frame is visible and
 * loaded, so nothing ever flashes an empty black iframe.
 */
function TemplateThumb({ media, className }: { media: Media; className: string }) {
  const [visible, setVisible] = useState(false);
  const [html, setHtml] = useState<string | null>(null);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el || visible) return;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setVisible(true);
          io.disconnect();
        }
      },
      { rootMargin: '150px' },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [visible]);

  useEffect(() => {
    if (!visible) return;
    const controller = new AbortController();
    fetch(mediaFileUrl(media.id), { credentials: 'include', signal: controller.signal })
      .then((r) => (r.ok ? r.text() : Promise.reject(new Error('load failed'))))
      .then(setHtml)
      .catch(() => {
        /* best-effort preview — a failed load just keeps the kind-tile glyph */
      });
    return () => controller.abort();
  }, [visible, media.id]);

  return (
    <div className={className} ref={ref}>
      {html != null ? (
        <ScaledFrame
          title={media.filename ?? 'Template preview'}
          referrerPolicy="no-referrer"
          srcDoc={html}
        />
      ) : (
        <span className="kind-ico" data-kind={media.kind} aria-hidden>
          <HtmlKindGlyph kind="html_template" />
        </span>
      )}
    </div>
  );
}

/**
 * Thumbnail for a media item (docs/10 #2.2). Both images and video request
 * `?thumb=1` — the server returns the generated thumb, or the original file if one
 * doesn't exist yet (docs/03 §Media). List responses don't carry `has_thumb`, so the
 * grid can't know up front; it just tries the thumb URL and reacts to onError:
 *   - image: falls back to the plain (non-thumb) file URL, then to the kind icon.
 *   - video without a thumb: the "original file" fallback is the raw video byte
 *     stream, which <img> can never render — so its onError goes straight to the
 *     kind icon instead of chasing an always-broken source.
 * When `has_thumb === false` is known (e.g. the detail drawer), video skips the
 * network round-trip entirely and shows the icon right away.
 */
export function MediaThumb({ media, className = 'media-thumb' }: { media: Media; className?: string }) {
  const [stage, setStage] = useState<'thumb' | 'original' | 'icon'>('thumb');

  useEffect(() => setStage('thumb'), [media.id]);

  const icon = (
    <span className="kind-ico" data-kind={media.kind} aria-hidden>
      <KindGlyph kind={media.kind} />
    </span>
  );

  // Generated HTML media (docs/09 §B). A ready html_template renders its real,
  // current slide in the media grid (className === 'media-thumb' — the default
  // used by MediaPage's tiles and the detail drawer; other callers pass their
  // own className for small picker thumbnails, where a live iframe per row would
  // be wasteful). html_snippet is raw code, not a visual slide, so it always
  // stays a styled kind-tile with the template/snippet name.
  if (isHtmlKind(media.kind)) {
    if (media.kind === 'html_template' && media.status === 'ready' && className === 'media-thumb') {
      return <TemplateThumb media={media} className={className} />;
    }
    return (
      <div className={`${className} kind-tile`}>
        <span className="kind-tile-ico">
          <HtmlKindGlyph kind={media.kind} />
        </span>
        <span className="kind-tile-label">{HTML_TILE_LABEL[media.kind]}</span>
      </div>
    );
  }

  if (media.kind === 'image' && media.status === 'ready') {
    if (stage === 'icon') return <div className={className}>{icon}</div>;
    return (
      <div className={className}>
        <img
          src={stage === 'thumb' ? mediaFileUrl(media.id, { thumb: true }) : mediaFileUrl(media.id)}
          alt={media.filename ?? 'image'}
          loading="lazy"
          onError={() => setStage((s) => (s === 'thumb' ? 'original' : 'icon'))}
        />
      </div>
    );
  }

  if (media.kind === 'video' && media.status === 'ready') {
    if (media.has_thumb === false || stage === 'icon') return <div className={className}>{icon}</div>;
    return (
      <div className={className}>
        <img
          src={mediaFileUrl(media.id, { thumb: true })}
          alt={media.filename ?? 'video'}
          loading="lazy"
          onError={() => setStage('icon')}
        />
      </div>
    );
  }

  return <div className={className}>{icon}</div>;
}

/** Lightweight preview of a single item scaled into a box (playlist preview). */
export function ItemPreview({ media }: { media: Media | undefined }) {
  if (!media) return <div className="ph">No item</div>;
  if (media.kind === 'image') {
    return <img src={mediaFileUrl(media.id)} alt={media.filename ?? ''} />;
  }
  if (media.kind === 'video') {
    // Mock has no real bytes; show a poster-style placeholder rather than a broken video.
    return (
      <div className="ph">
        <span className="kind-ico" data-kind="video" aria-hidden>
          <KindGlyph kind="video" />
        </span>
        {media.filename}
      </div>
    );
  }
  // kind=url — render the page in an iframe (same as the real player).
  return <iframe title={media.filename ?? 'url'} src={media.src_url ?? undefined} />;
}
